import { useRef } from 'react';

import { useTimedCallback } from '@xing-com/crate-jobs-hooks';

const DELAY_MS = 40;
const DELAY_MIN_MS = 1;
const ACCELERATION_START_CHARS = 20;
const ACCELERATION_MAX_REACHED = 0.5;
const EXPONENTIAL_BASE = Math.pow(
  DELAY_MIN_MS / DELAY_MS,
  1 / ACCELERATION_MAX_REACHED
);

const getDelay = (iter: number, total: number): number => {
  if (iter < ACCELERATION_START_CHARS) {
    return DELAY_MS;
  }

  const progress =
    (iter - ACCELERATION_START_CHARS) / (total - ACCELERATION_START_CHARS);
  return Math.max(
    DELAY_MIN_MS,
    DELAY_MS * Math.pow(EXPONENTIAL_BASE, progress)
  );
};

type AnimationOptions = {
  onCompleted?: () => void;
};

export const useAnimatedValue = (
  ref: React.RefObject<HTMLTextAreaElement | null>,
  value: string,
  { onCompleted }: AnimationOptions = {}
  // oxlint-disable-next-line typescript/explicit-function-return-type
) => {
  const indexRef = useRef(0);
  const { run, cancel } = useTimedCallback({ speed: DELAY_MS });

  const type = (): void => {
    if (!ref.current) {
      return;
    }

    if (indexRef.current >= value.length) {
      stop();
      return;
    }

    ref.current.value = value.slice(0, indexRef.current + 1);
    ++indexRef.current;

    run(type, { delay: getDelay(indexRef.current, value.length) });
  };

  const stop = (): void => {
    onCompleted?.();
    cancel();
    indexRef.current = 0;
    if (ref.current) {
      ref.current.value = '';
    }
  };

  return { run: type, cancel: stop };
};
